import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {FieldErrors} from 'react-hook-form'
import Table from '@material-ui/core/Table'
import {
  Checkbox,
  TableBody,
  TableHead,
  TableRow,
  makeStyles,
} from '@material-ui/core'
import TableCell from 'components/Table/TableCell'
import {ProductSchema} from 'pages/ProductsPages/ProductsDetail'
import {
  TABLE_HEADER_DETAIL,
  TYPE_INDICATOR_PRODUCT,
  UNIT_PRICES,
} from 'pages/ProductsPages/constant'
import {DisplayInfosType, ListPackageResponse} from 'api/productApi'
import ProductsDetailStyle from 'pages/ProductsPages/ProductsDetail/Style'
import {formatPriceNumber} from 'helpers/uiHelper'

const useStyles = makeStyles(ProductsDetailStyle)

interface IndicatorProductProps {
  listPackage: ListPackageResponse[]
  typeIndicator: string
  disabledAccordingPaymentMethod: boolean
  disabledAll?: boolean
  errors: FieldErrors<ProductSchema>
  handleChooseType: (id: number | string) => void
  setUnitPrices: (id: string, value: string, key: string) => void
  setOfferAmount: (id: string, value: string) => void
  disabledByManualPayment: boolean
}

const IndicatorProduct = ({
  listPackage,
  typeIndicator,
  disabledAccordingPaymentMethod,
  disabledAll = false,
  errors,
  handleChooseType,
  setUnitPrices,
  setOfferAmount,
  disabledByManualPayment,
}: IndicatorProductProps) => {
  const classes = useStyles()
  const {t, i18n} = useTranslation()

  const getProductName = (data: DisplayInfosType[]) =>
    data.find(
      (item) => item.langCode === (i18n.language === 'ko' ? 'ko' : 'en'),
    )?.name || ''

  const getTypeIndicatorProduct = (target: string) => {
    let value = ''
    if (typeIndicator !== TYPE_INDICATOR_PRODUCT.ALL) return value
    switch (target) {
      case TYPE_INDICATOR_PRODUCT.HOSPITAL:
        value = ` (${t('IHospitals')}) `
        break
      case TYPE_INDICATOR_PRODUCT.HEALTH:
        value = ` (${t('IHealthcare')}) `
        break
      case TYPE_INDICATOR_PRODUCT.ALL:
        break
      default:
        break
    }
    return value
  }

  const data = useMemo(() => {
    const value = listPackage.toSorted((a, b) => a.order - b.order)

    return value
  }, [listPackage])

  const rowSpanMap = (data ?? []).reduce<Record<string, number>>(
    (acc, curr) => {
      acc[curr.cls] = (acc[curr.cls] || 0) + 1
      return acc
    },
    {},
  )
  const seenCls = new Set<string>()

  const getValuePrices = (id: number | string, key: string) => {
    let value = 0
    listPackage.forEach((item) => {
      if (item.itemId === id) {
        item.unitPrices.forEach((e) => {
          if (e.currency === key) {
            value = e.price
          }
        })
      }
    })
    return formatPriceNumber(value)
  }

  const isDisablePairProduct = (item: ListPackageResponse) => {
    const pairedIds = [
      ['3', '4'],
      ['9', '10'],
    ]

    const pair = pairedIds.find((p) => p.includes(item.itemId.toString()))
    if (!pair) return false

    const otherItemId = pair.find((id) => id !== item.itemId.toString())
    return listPackage.some(
      (pkg) => pkg.itemId.toString() === otherItemId && pkg.select,
    )
  }

  const onChangePrice = (id: string, value: string, currency: string) => {
    const digitsOnly = value.replace(/[^0-9]/g, '')
    setUnitPrices(id, digitsOnly, currency)
  }

  return (
    <Table
      size='small'
      className={classes.table}
      style={{tableLayout: 'fixed'}}
    >
      <TableHead className={classes.tableHeader}>
        {TABLE_HEADER_DETAIL(t).map((item) => (
          <TableCell
            align='center'
            padding='checkbox'
            key={item.id}
            className={classes.headerMax}
            style={{width: item.width || 120}}
          >
            {item.label}
          </TableCell>
        ))}
      </TableHead>
      <TableBody>
        {data?.map((item, index: number) => {
          const isFirstOccurCls = !seenCls.has(item.cls)
          if (isFirstOccurCls) seenCls.add(item.cls)
          return (
            <TableRow key={item.itemId}>
              {index === 0 && (
                <TableCell
                  align='center'
                  padding='checkbox'
                  rowSpan={listPackage?.length ?? 0}
                  className={`${classes.headerMax} ${classes.bgItem}`}
                  style={{width: 120}}
                >
                  {t('ICategory')}
                </TableCell>
              )}

              {isFirstOccurCls && (
                <TableCell
                  align='center'
                  padding='checkbox'
                  rowSpan={rowSpanMap[item.cls]}
                  className={classes.headerMax}
                  style={{width: 120}}
                >
                  {item.cls}
                </TableCell>
              )}

              <TableCell
                padding='checkbox'
                align='center'
                className={classes.headerMax}
              >
                <Checkbox
                  checked={item.select}
                  color='primary'
                  onChange={() => handleChooseType(item.itemId)}
                  disabled={disabledAll}
                />
              </TableCell>
              <TableCell
                align='left'
                padding='checkbox'
                className={classes.headerMax}
                style={{width: 120}}
              >
                {getProductName(item.displayInfos)}
                {getTypeIndicatorProduct(item.target)}
                {item.cls === 'PBM' && <> - {t('IMinute')}</>}
              </TableCell>
              <TableCell
                align='center'
                padding='checkbox'
                className={classes.headerMax}
                style={{width: 120}}
              >
                <input
                  className={classes.input}
                  value={getValuePrices(item.itemId, UNIT_PRICES.KRW)}
                  disabled={!item.select || disabledAll}
                  type='string'
                  onChange={(e) => {
                    onChangePrice(
                      item.itemId.toString(),
                      e.target.value,
                      UNIT_PRICES.KRW,
                    )
                  }}
                  min={0}
                />
              </TableCell>
              <TableCell
                align='center'
                padding='checkbox'
                className={classes.headerMax}
                style={{width: 120}}
              >
                <input
                  className={classes.input}
                  value={getValuePrices(item.itemId, UNIT_PRICES.USD)}
                  disabled={!item.select || disabledAll}
                  type='string'
                  onChange={(e) => {
                    onChangePrice(
                      item.itemId.toString(),
                      e.target.value,
                      UNIT_PRICES.USD,
                    )
                  }}
                  min={0}
                />
              </TableCell>
              <TableCell
                align='center'
                padding='checkbox'
                className={classes.headerMax}
                style={{width: 120}}
              >
                <input
                  className={classes.input}
                  value={item.offerAmount ?? 0}
                  disabled={
                    disabledAccordingPaymentMethod ||
                    !item.select ||
                    disabledAll ||
                    disabledByManualPayment
                  }
                  type='string'
                  onChange={(e) => {
                    setOfferAmount(item.itemId.toString(), e.target.value)
                  }}
                  min={0}
                />
              </TableCell>
              <TableCell
                align='center'
                padding='checkbox'
                className={classes.headerMax}
                style={{width: 120}}
              >
                {}
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
      {errors.offerItems?.message && (
        <p style={{color: 'red', whiteSpace: 'pre', paddingTop: 5}}>
          {errors.offerItems.message}
        </p>
      )}
    </Table>
  )
}

export default IndicatorProduct
